// Footer3.js - Contains functions for creating a split-layout Footer section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a Footer section with a modern split layout
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the footer is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The footer elements
 */
export const createFooterElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const footerSectionClass = 'footer3-section';
  const footerContainerClass = 'footer3-container';
  const footerTopClass = 'footer3-top';
  const footerLeftClass = 'footer3-left';
  const footerRightClass = 'footer3-right';
  const footerLogoClass = 'footer3-logo';
  const footerTaglineClass = 'footer3-tagline';
  const footerNewsletterClass = 'footer3-newsletter';
  const footerFormClass = 'footer3-form';
  const footerInputClass = 'footer3-input';
  const footerButtonClass = 'footer3-button';
  const footerDividerClass = 'footer3-divider';
  const footerBottomClass = 'footer3-bottom';
  const footerNavClass = 'footer3-nav';
  const footerLinkClass = 'footer3-link';
  const footerSocialClass = 'footer3-social';
  const footerSocialLinkClass = 'footer3-social-link';
  const footerCopyrightClass = 'footer3-copyright';

  // Desktop Styles (default)
  const footerSectionStyles = {
    position: 'relative',
    paddingTop: '60px',
    paddingBottom: '40px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f8f9fa',
    color: '#212529',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderTop: '1px solid #e9ecef'
  };

  const footerContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    maxWidth: '1200px',
    width: '100%',
    marginTop: '0',
    marginBottom: '0',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '40px'
  };

  const footerTopStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
    gap: '60px'
  };

  const footerLeftStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flex: '1'
  };

  const footerRightStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    flex: '1'
  };

  const footerLogoStyles = {
    fontSize: '26px',
    fontWeight: '700',
    color: '#212529',
    textDecoration: 'none',
    display: 'inline-block'
  };

  const footerTaglineStyles = {
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#6c757d',
    maxWidth: '380px'
  };

  const footerNewsletterStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  };

  const footerFormStyles = {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px'
  };

  const footerInputStyles = {
    flex: '1',
    paddingTop: '12px',
    paddingBottom: '12px',
    paddingLeft: '16px',
    paddingRight: '16px',
    fontSize: '15px',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    backgroundColor: '#ffffff',
    color: '#495057'
  };

  const footerButtonStyles = {
    paddingTop: '12px',
    paddingBottom: '12px',
    paddingLeft: '20px',
    paddingRight: '20px',
    fontSize: '15px',
    backgroundColor: '#0d6efd',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'background-color 0.2s ease'
  };

  const footerDividerStyles = {
    height: '1px',
    backgroundColor: '#e9ecef',
    width: '100%',
    marginTop: '10px',
    marginBottom: '10px',
    marginLeft: '0',
    marginRight: '0'
  };

  const footerBottomStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    flexWrap: 'wrap',
    gap: '20px'
  };

  const footerNavStyles = {
    display: 'flex',
    flexDirection: 'row',
    gap: '24px',
    flexWrap: 'wrap'
  };

  const footerLinkStyles = {
    fontSize: '15px',
    color: '#495057',
    textDecoration: 'none',
    transition: 'color 0.2s ease'
  };

  const footerSocialStyles = {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px'
  };

  const footerSocialLinkStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: '6px',
    color: '#ffffff',
    backgroundColor: '#495057',
    transition: 'background-color 0.2s ease'
  };

  const footerCopyrightStyles = {
    fontSize: '14px',
    color: '#6c757d',
    width: '100%',
    textAlign: 'center',
    marginTop: '20px'
  };

  // Tablet Styles
  const tabletFooterTopStyles = {
    ...footerTopStyles,
    flexWrap: 'wrap',
    gap: '40px'
  };

  // Mobile Styles
  const mobileFooterTopStyles = {
    ...footerTopStyles,
    flexDirection: 'column',
    gap: '40px'
  };

  const mobileFooterBottomStyles = {
    ...footerBottomStyles,
    flexDirection: 'column',
    alignItems: 'center',
    gap: '24px'
  };

  const mobileFooterFormStyles = {
    ...footerFormStyles,
    flexDirection: 'column'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(footerSectionClass, footerSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(footerContainerClass, footerContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(footerTopClass, footerTopStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLeftClass, footerLeftStyles);
    newRegistry[DEVICES.DESKTOP].set(footerRightClass, footerRightStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLogoClass, footerLogoStyles);
    newRegistry[DEVICES.DESKTOP].set(footerTaglineClass, footerTaglineStyles);
    newRegistry[DEVICES.DESKTOP].set(footerNewsletterClass, footerNewsletterStyles);
    newRegistry[DEVICES.DESKTOP].set(footerFormClass, footerFormStyles);
    newRegistry[DEVICES.DESKTOP].set(footerInputClass, footerInputStyles);
    newRegistry[DEVICES.DESKTOP].set(footerButtonClass, footerButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(footerDividerClass, footerDividerStyles);
    newRegistry[DEVICES.DESKTOP].set(footerBottomClass, footerBottomStyles);
    newRegistry[DEVICES.DESKTOP].set(footerNavClass, footerNavStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLinkClass, footerLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(footerSocialClass, footerSocialStyles);
    newRegistry[DEVICES.DESKTOP].set(footerSocialLinkClass, footerSocialLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(footerCopyrightClass, footerCopyrightStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(footerTopClass, tabletFooterTopStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(footerTopClass, mobileFooterTopStyles);
    newRegistry[DEVICES.MOBILE].set(footerBottomClass, mobileFooterBottomStyles);
    newRegistry[DEVICES.MOBILE].set(footerFormClass, mobileFooterFormStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    footerSectionClass,
    footerContainerClass,
    footerTopClass,
    footerLeftClass,
    footerRightClass,
    footerLogoClass,
    footerTaglineClass,
    footerNewsletterClass,
    footerFormClass,
    footerInputClass,
    footerButtonClass,
    footerDividerClass,
    footerBottomClass,
    footerNavClass,
    footerLinkClass,
    footerSocialClass,
    footerSocialLinkClass,
    footerCopyrightClass
  ]));

  // Generate IDs for all elements
  const footerSectionId = Date.now();
  const containerID = Date.now() + 1;
  const topID = Date.now() + 2;
  
  // Left section
  const leftID = Date.now() + 3;
  const logoID = Date.now() + 4;
  const taglineID = Date.now() + 5;
  
  // Right section - Newsletter
  const rightID = Date.now() + 6;
  const newsletterID = Date.now() + 7;
  const newsletterHeadingID = Date.now() + 8;
  const formID = Date.now() + 9;
  const inputID = Date.now() + 10;
  const buttonID = Date.now() + 11;
  
  // Divider
  const dividerID = Date.now() + 12;
  
  // Bottom section
  const bottomID = Date.now() + 13;
  const navID = Date.now() + 14;
  const link1ID = Date.now() + 15;
  const link2ID = Date.now() + 16;
  const link3ID = Date.now() + 17;
  const link4ID = Date.now() + 18;
  
  // Social links
  const socialWrapperID = Date.now() + 19;
  const social1ID = Date.now() + 20;
  const social2ID = Date.now() + 21;
  const social3ID = Date.now() + 22;
  
  // Copyright
  const copyrightID = Date.now() + 23;

  // Generate IDs for social media icons
  const social1IconId = Date.now() + 24;
  const social2IconId = Date.now() + 25;
  const social3IconId = Date.now() + 26;

  const newElements = [
    // Main Footer Section Container
    {
      id: footerSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Footer Section',
      parentId: null,
      className: footerSectionClass,
      styles: {},
      children: [containerID],
      text: '',
      attributes: {
        'data-element': 'footer-section',
        'data-component': 'section',
        'data-section-type': 'footer',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Container',
      parentId: footerSectionId,
      hierarchyName: "Footer Container",
      className: footerContainerClass,
      styles: {},
      children: [topID, dividerID, bottomID, copyrightID],
      text: '',
      attributes: {
        'data-element': 'footer-container'
      }
    },
    
    // Top Section
    {
      id: topID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Top',
      parentId: containerID,
      hierarchyName: "Footer Top",
      className: footerTopClass,
      styles: {},
      children: [leftID, rightID],
      text: '',
      attributes: {
        'data-element': 'footer-top'
      }
    },
    
    // Left Section
    {
      id: leftID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Left',
      parentId: topID,
      hierarchyName: "Footer Left",
      className: footerLeftClass,
      styles: {},
      children: [logoID, taglineID],
      text: '',
      attributes: {
        'data-element': 'footer-left'
      }
    },
    
    // Logo
    {
      id: logoID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Footer Logo',
      parentId: leftID,
      hierarchyName: "Footer Logo",
      className: footerLogoClass,
      styles: {},
      children: [],
      text: 'Horizon',
      attributes: {
        'data-element': 'footer-logo',
        'href': '#'
      }
    },
    
    // Tagline
    {
      id: taglineID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Footer Tagline',
      parentId: leftID,
      hierarchyName: "Footer Tagline",
      className: footerTaglineClass,
      styles: {},
      children: [],
      text: 'Empowering creators and businesses with tools to build beautiful experiences and connect with their audience.',
      attributes: {
        'data-element': 'footer-tagline'
      }
    },
    
    // Right Section
    {
      id: rightID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Right',
      parentId: topID,
      hierarchyName: "Footer Right",
      className: footerRightClass,
      styles: {},
      children: [newsletterID],
      text: '',
      attributes: {
        'data-element': 'footer-right'
      }
    },
    
    // Newsletter
    {
      id: newsletterID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Newsletter',
      parentId: rightID,
      hierarchyName: "Newsletter",
      className: footerNewsletterClass,
      styles: {},
      children: [newsletterHeadingID, formID],
      text: '',
      attributes: {
        'data-element': 'footer-newsletter'
      }
    },
    
    // Newsletter Heading
    {
      id: newsletterHeadingID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Newsletter Heading',
      parentId: newsletterID,
      hierarchyName: "Newsletter Heading",
      className: '',
      styles: {
        fontSize: '18px',
        fontWeight: '600',
        marginBottom: '12px',
        color: '#212529'
      },
      children: [],
      text: 'Subscribe to our newsletter',
      attributes: {
        'data-element': 'newsletter-heading'
      }
    },
    
    // Newsletter Form
    {
      id: formID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'form',
      name: 'Newsletter Form',
      parentId: newsletterID,
      hierarchyName: "Newsletter Form",
      className: footerFormClass,
      styles: {},
      children: [inputID, buttonID],
      text: '',
      attributes: {
        'data-element': 'newsletter-form'
      }
    },
    
    // Email Input
    {
      id: inputID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Email Input',
      parentId: formID,
      hierarchyName: "Email Input",
      className: footerInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'email-input',
        'type': 'email',
        'placeholder': 'Your email address',
        'required': 'true'
      }
    },
    
    // Submit Button
    {
      id: buttonID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Subscribe Button',
      parentId: formID,
      hierarchyName: "Subscribe Button",
      className: footerButtonClass,
      styles: {},
      children: [],
      text: 'Subscribe',
      attributes: {
        'data-element': 'subscribe-button',
        'type': 'submit'
      }
    },
    
    // Divider
    {
      id: dividerID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Divider',
      parentId: containerID,
      hierarchyName: "Footer Divider",
      className: footerDividerClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'footer-divider'
      }
    },
    
    // Bottom Section
    {
      id: bottomID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Bottom',
      parentId: containerID,
      hierarchyName: "Footer Bottom",
      className: footerBottomClass,
      styles: {},
      children: [navID, socialWrapperID],
      text: '',
      attributes: {
        'data-element': 'footer-bottom'
      }
    },
    
    // Navigation
    {
      id: navID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'nav',
      name: 'Footer Navigation',
      parentId: bottomID,
      hierarchyName: "Footer Navigation",
      className: footerNavClass,
      styles: {},
      children: [link1ID, link2ID, link3ID, link4ID],
      text: '',
      attributes: {
        'data-element': 'footer-nav',
        'aria-label': 'Footer Navigation'
      }
    },
    
    // Nav Link 1
    {
      id: link1ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Nav Link 1',
      parentId: navID,
      hierarchyName: "Nav Link 1",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Terms of Service',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Nav Link 2
    {
      id: link2ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Nav Link 2',
      parentId: navID,
      hierarchyName: "Nav Link 2",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Privacy Policy',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Nav Link 3
    {
      id: link3ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Nav Link 3',
      parentId: navID,
      hierarchyName: "Nav Link 3",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'FAQ',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Nav Link 4
    {
      id: link4ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Nav Link 4',
      parentId: navID,
      hierarchyName: "Nav Link 4",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Contact',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Social Links
    {
      id: socialWrapperID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Social Links',
      parentId: bottomID,
      hierarchyName: "Social Links",
      className: footerSocialClass,
      styles: {},
      children: [social1ID, social2ID, social3ID],
      text: '',
      attributes: {
        'data-element': 'footer-social'
      }
    },
    
    // Social Link 1
    {
      id: social1ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Social Link 1',
      parentId: socialWrapperID,
      hierarchyName: "Social Link 1",
      className: footerSocialLinkClass,
      styles: {},
      children: [social1IconId],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'aria-label': 'Twitter'
      }
    },

    // Twitter Icon
    {
      id: social1IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Twitter Icon',
      parentId: social1ID,
      hierarchyName: "Twitter Icon",
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:twitter.svg?color=%23ffffff',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Twitter'
      }
    },
    
    // Social Link 2
    {
      id: social2ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Social Link 2',
      parentId: socialWrapperID,
      hierarchyName: "Social Link 2",
      className: footerSocialLinkClass,
      styles: {},
      children: [social2IconId],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'aria-label': 'Instagram'
      }
    },

    // Instagram Icon
    {
      id: social2IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Instagram Icon',
      parentId: social2ID,
      hierarchyName: "Instagram Icon",
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:instagram.svg?color=%23ffffff',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Instagram'
      }
    },
    
    // Social Link 3
    {
      id: social3ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Social Link 3',
      parentId: socialWrapperID,
      hierarchyName: "Social Link 3",
      className: footerSocialLinkClass,
      styles: {},
      children: [social3IconId],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'aria-label': 'LinkedIn'
      }
    },

    // LinkedIn Icon
    {
      id: social3IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'LinkedIn Icon',
      parentId: social3ID,
      hierarchyName: "LinkedIn Icon",
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:linkedin.svg?color=%23ffffff',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'LinkedIn'
      }
    },
    
    // Copyright
    {
      id: copyrightID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Footer Copyright',
      parentId: containerID,
      hierarchyName: "Footer Copyright",
      className: footerCopyrightClass,
      styles: {},
      children: [],
      text: `© ${new Date().getFullYear()} Horizon. All rights reserved.`,
      attributes: {
        'data-element': 'footer-copyright'
      }
    }
  ];
  
  // Return all elements
  return {
    footerSection: newElements[0],
    elements: newElements
  };
};

export default createFooterElements; 